/**
 * WAF threat / mitigation analytics (Owl `mitigationReport`). Two diagrams for a
 * single domain (subdomain FQDN), service-scoped (path `/services/:service_id/waf/…`):
 *  - `GET /services/:service_id/waf/threat/diagram/summary` → per-attack-category counts (no time axis).
 *  - `GET /services/:service_id/waf/threat/diagram/top`     → top-N (IP|country) time series.
 * `module` is a CSV of mitigation modules; `metric` is `request | traffic`.
 */
import { useQuery } from '@tanstack/react-query';
import { serviceApiV3, wafKeys, unwrap, unwrapList } from './_shared';

export type WafThreatMetric = 'request' | 'traffic';
export type WafThreatDimension = 'topIp' | 'topCountry';

/** Summary diagram: parallel `labels` (attack category) + `datasets` (count). */
export interface WafThreatSummary {
  labels: string[];
  datasets: number[];
}

/** One ranked entity's mitigation time series (per-bucket count + category detail). */
export interface WafThreatTopSeries {
  label: string;
  total_request?: number[];
  total_traffic?: number[];
  detail: Record<string, number>[];
}

/** Top-N diagram: shared x-axis (`labels`, unix ms) + one series per entity. */
export interface WafThreatTopDiagram {
  labels: number[];
  datasets: WafThreatTopSeries[];
}

export interface WafThreatSummaryFilter {
  domain: string;
  start_time: number;
  end_time: number;
  /** Mitigation modules CSV (e.g. "Firewall rules,CRS rules"); omitted = all. */
  module?: string;
  metric?: WafThreatMetric;
}

export interface WafThreatTopFilter extends WafThreatSummaryFilter {
  dimension: WafThreatDimension;
  /** Bucket seconds (0/omitted = BE auto-pick). */
  interval?: number;
}

/** Per-attack-category mitigation summary (donut + metric grid). */
export function useWafThreatSummary(
  serviceId: string | undefined,
  filter: WafThreatSummaryFilter,
  enabled = true,
) {
  const params = { ...filter };
  return useQuery<WafThreatSummary>({
    queryKey: wafKeys.threatSummary(serviceId ?? '', params as Record<string, unknown>),
    enabled: !!serviceId && !!filter.domain && enabled,
    staleTime: 30_000,
    queryFn: async () =>
      unwrap<WafThreatSummary>(
        await serviceApiV3.get(`/services/${serviceId}/waf/threat/diagram/summary`, { params }),
      ),
  });
}

/** Top-N mitigation time series by IP or country (chart + ranked table). */
export function useWafThreatTopDiagram(
  serviceId: string | undefined,
  filter: WafThreatTopFilter,
  enabled = true,
) {
  const params = { ...filter };
  return useQuery<WafThreatTopDiagram>({
    queryKey: wafKeys.threatTop(serviceId ?? '', params as Record<string, unknown>),
    enabled: !!serviceId && !!filter.domain && !!filter.dimension && enabled,
    staleTime: 30_000,
    queryFn: async () =>
      unwrap<WafThreatTopDiagram>(
        await serviceApiV3.get(`/services/${serviceId}/waf/threat/diagram/top`, { params }),
      ),
  });
}

// ---- Threat Log (incident explorer) -------------------------------------
// Distinct from the *diagram* endpoints above: `/waf/threat/summary` is a flat
// per-category count list (rendered as cards) and `/waf/threat/incidents` is the
// paginated raw event log (rendered as a table with a per-row detail dialog).

/** One per-category mitigation count (Threat Log card). */
export interface WafThreatReportEntry {
  category: string;
  total_request: number;
}

/** One matched WAF/CRS rule inside an incident's mitigation verdict. */
export interface WafThreatHit {
  category: string;
  match_key: string;
  match_val: string;
  severity: number;
  msg: string;
  id: number;
}

/** Mitigation verdict for an incident (combined score + matched rules). */
export interface WafThreatMsg {
  action: string;
  score: number;
  hit: WafThreatHit[];
}

/** One mitigation incident / request event (full row + detail dialog source). */
export interface WafThreatIncident {
  incident_id: string;
  category: string;
  module: string;
  domain: string;
  request_host: string;
  uri: string;
  query_string: string;
  request_method: string;
  server_protocol: string;
  scheme: string;
  ip: string;
  http_x_forwarded_for: string;
  http_user_agent: string;
  country: string;
  sender: string;
  action: string;
  severity: number;
  request_time: string;
  time: number;
  msg: WafThreatMsg;
  detail?: Record<string, unknown>;
}

export interface WafThreatReportFilter {
  domain: string;
  start_time: number;
  end_time: number;
  /** Narrow to one client IP. */
  ip?: string;
  /** Narrow to one incident / request id. */
  request_id?: string;
}

export interface WafThreatIncidentsFilter extends WafThreatReportFilter {
  /** Mitigation category filter (from a selected card). */
  category?: string;
  page?: number;
  page_size?: number;
}

/** Per-category mitigation counts for the Threat Log cards. */
export function useWafThreatReportSummary(
  serviceId: string | undefined,
  filter: WafThreatReportFilter,
  enabled = true,
) {
  const params = { ...filter };
  return useQuery<WafThreatReportEntry[]>({
    queryKey: wafKeys.threatReportSummary(serviceId ?? '', params as Record<string, unknown>),
    enabled: !!serviceId && !!filter.domain && enabled,
    staleTime: 30_000,
    queryFn: async () =>
      unwrap<WafThreatReportEntry[]>(
        await serviceApiV3.get(`/services/${serviceId}/waf/threat/summary`, { params }),
      ) ?? [],
  });
}

/** Paginated mitigation incident log (Threat Log table). */
export function useWafThreatIncidents(
  serviceId: string | undefined,
  filter: WafThreatIncidentsFilter,
  enabled = true,
) {
  const params = { ...filter };
  return useQuery({
    queryKey: wafKeys.threatIncidents(serviceId ?? '', params as Record<string, unknown>),
    enabled: !!serviceId && !!filter.domain && enabled,
    staleTime: 30_000,
    queryFn: async () =>
      unwrapList<WafThreatIncident>(await serviceApiV3.get(`/services/${serviceId}/waf/threat/incidents`, { params })),
  });
}
